import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { FileSelectComponent, SelectEvent } from '@progress/kendo-angular-upload';
import { ToastrService } from 'ngx-toastr';
import { Observable, take, tap } from 'rxjs';
import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { CustomSellerInfo } from 'src/app/common/CustomSellerInfo';
import { AppProfileService } from 'src/app/services/profile.service';
import { CustomSellerInfoService } from 'src/app/services/custom-seller-info.service';
import { KENDO_BUTTONS, ButtonGroupModule, ButtonsModule } from '@progress/kendo-angular-buttons';
import { KENDO_TOOLBAR } from '@progress/kendo-angular-toolbar';
import { KENDO_EDITOR } from '@progress/kendo-angular-editor';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { readAsDataUrl } from 'src/app/common/browser-files';

/** The seller info editor; its type is inferred from here (#199). */
function buildSellerInfoForm(fb: FormBuilder, info: CustomSellerInfo) {
  return fb.group({
    editor: [info?.brandInfoText],
    // The Kendo file picker's value: the selected files, none to start with.
    files: [[] as File[]],
  });
}
type SellerInfoForm = ReturnType<typeof buildSellerInfoForm>;

@Component({
    imports: [
    ReactiveFormsModule,
    KENDO_BUTTONS,
    FileSelectComponent,
    ButtonGroupModule,
    ButtonsModule,
    KENDO_TOOLBAR,
    KENDO_EDITOR,
    AsyncClickDirective
],
    selector: 'app-custom-seller-info-edit',
    templateUrl: './custom-seller-info-edit.component.html'
})
export class CustomSellerInfoEditComponent implements OnInit {
  private fb = inject(FormBuilder);
  private profileService = inject(AppProfileService);
  private customSellerInfoService = inject(CustomSellerInfoService);
  private toastr = inject(ToastrService);
  private apiActionService = inject(CallApiService);

  public myForm: SellerInfoForm;
  public showEdit: boolean = false;
  public customSellerInfo: CustomSellerInfo;
  private profile: ExtendedProfile;
  public files: Array<File> = [];
  public submitted = false;
  public imageFile: any;

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    // The profile once: it says whose seller info this is. Each save used to subscribe to it
    // again, for good, so every later profile refresh reloaded the info once per save (#199).
    this.profileService.currentProfile$
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((p) => {
        this.profile = p;
        this.refreshAndStopEditMode();
      });
  }

  public toggleEdit() {
    this.showEdit = !this.showEdit;
  }

  public selectImageEventHandler(e: SelectEvent): void {
    readAsDataUrl(e.files[0].rawFile, (url) => (this.imageFile = url));
  }

  public save(): Observable<unknown> {
    this.customSellerInfo.brandInfoText = this.myForm.controls.editor.value;

    if (this.customSellerInfo.id) {
      return this.apiActionService
        .callWithToasts$(
          this.customSellerInfoService.update(this.customSellerInfo),
          $localize`Anbieterinformationen wurden aktualisiert`,
          $localize`Bei der Aktualisierung der Anbieterinformationen ist ein Fehler aufgetreten`
        )
        .pipe(
          tap(() => {
            this.files = this.myForm.controls.files.value;
            this.SaveBrandImageIfChanged();
            this.refreshAndStopEditMode();
          })
        );
    }

    return this.apiActionService
      .callWithToasts$(
        this.customSellerInfoService.create(this.customSellerInfo),
        $localize`Anbieterinformationen wurden gespeichert`,
        $localize`Beim Speichern der Anbieterinformationen ist ein Fehler aufgetreten`
      )
      .pipe(tap(() => this.refreshAndStopEditMode()));
  }

  public cancelEdit() {
    this.refreshAndStopEditMode();
  }

  private SaveBrandImageIfChanged() {
    if (this.files[0]) {
      this.apiActionService.callWithToasts(
        this.customSellerInfoService.updateBrandImage(this.customSellerInfo.customerId, this.files[0]),
        $localize`Das Anbieterbild wurde aktualisiert.`,
        $localize`Beim Aktualisieren des Anbieterbildes ist ein Fehler aufgetreten.`
      );
    }
  }

  private refreshAndStopEditMode() {
    this.Load();
    this.showEdit = false;
    this.refreshForm();
  }

  private Load() {
    this.customSellerInfoService
      .getForCustomer(this.profile.customer.id)
      .pipe(take(1))
      .subscribe({
        next: (d) => {
          this.customSellerInfo = d;
          this.refreshForm();
          this.loadBrandImage();
        },
        error: (e: HttpErrorResponse) => {
          if (e.status === 404) {
            this.customSellerInfo = new CustomSellerInfo();
            this.customSellerInfo.customerId = this.profile.customer.id;
            this.refreshForm();
          } else {
            this.toastr.error($localize`Es konnten keine Anbieterinformationen geladen werden.`);
          }
        },
      });
  }

  private loadBrandImage() {
    this.customSellerInfoService
      .getBrandImage(this.profile.customer.id)
      .pipe(take(1))
      .subscribe((image) => readAsDataUrl(image, (url) => (this.imageFile = url)));
  }

  private refreshForm() {
    this.myForm = buildSellerInfoForm(this.fb, this.customSellerInfo);
  }

  public copyInputMessage(inputElement: HTMLInputElement) {
    navigator.clipboard
      .writeText(inputElement.value)
      .then()
      .catch((e) => console.error(e));
    this.toastr.info($localize`Registrierungslink wurde in die Zwischenablage kopiert`);
  }
}
